/**
 * Simple math helpers.
 * @module
 */

/**
 * linear interpolation between a and b
 */
export function lerp(a: number, b: number, t = 0.5): number {
  return b * t + a * (1 - t);
}

/**
 * Float remainder that returns a positive result even if the first parameter is negative.
 * Second parameter should be set to a positive number.
 */
export function fmod(a: number, b: number): number {
  return a - Math.floor(a / b) * b;
}

/**
 * Clamp a value between a lower and upper bound.
 */
export function clamp(value: number, lower: number, upper: number): number {
  return Math.max(lower, Math.min(upper, value));
}

/**
 * Clamp a value between 0 and 1.
 */
export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

/**
 * Closest integer power of two on a logarithmic scale.
 * @param roundingBias number in the -0.5 to 0.5 range to bias rounding the exponent. Use -0.5 to always round down, or 0.4999 to always round up.
 */
export function closestIntegerPowerOfTwo(a: number, roundingBias: number = 0): number {
  if (a < 1) {
    return 1;
  }
  const power = Math.log2(a);
  const powerOfTwo = 1 << Math.round(power + roundingBias);
  return powerOfTwo;
}

export function unsignedDeltaRadians(a: number, b: number): number {
  const w = 2 * Math.PI;
  a = fmod(a, w);
  b = fmod(b, w);
  return Math.min(Math.abs(a - b), Math.abs(a + w - b), Math.abs(a - w - b));
}

export function unsignedDeltaDegrees(a: number, b: number): number {
  const w = 360;
  a = fmod(a, w);
  b = fmod(b, w);
  return Math.min(Math.abs(a - b), Math.abs(a + w - b), Math.abs(a - w - b));
}

/**
 * Linearly interpolate two angles so that they wrap around the unit circle. Result will be between 0 and PI * 2.
 */
export function lerpRadians(a: number, b: number, t: number): number {
  const w = 2 * Math.PI;
  a = fmod(a, w);
  b = fmod(b, w);
  if (Math.abs(a - b) > w * 0.5) {
    if (a > b) {
      b += w;
    } else {
      a += w;
    }
  }
  return fmod(a * (1 - t) + b * t, w);
}

/**
 * Linearly interpolate two angles so that they wrap around the unit circle. Result will be between 0 and 360.
 */
export function lerpDegrees(a: number, b: number, t: number): number {
  const w = 2 * 180;
  a = fmod(a, w);
  b = fmod(b, w);
  if (Math.abs(a - b) > w * 0.5) {
    if (a > b) {
      b += w;
    } else {
      a += w;
    }
  }
  return fmod(a * (1 - t) + b * t, w);
}
export function degreeToRadian(angle: number): number {
  const rad = angle * (Math.PI / 180);
  return rad;
}

export function radianToDegree(angle: number): number {
  const deg = angle * (180 / Math.PI);
  return deg;
}

/**
 * Convert from linear [0,1] to logarithmic [min,max].
 */
export function linearToLogScale(value: number, min: number, max: number): number {
  const minLog = Math.log(min);
  const maxLog = Math.log(max);
  return Math.exp(minLog + (maxLog - minLog) * value);
}

/**
 * Convert from logarithmic [min,max] to linear [0,1].
 */
export function logToLinearScale(value: number, min: number, max: number): number {
  const minLog = Math.log(min);
  const maxLog = Math.log(max);
  return (Math.log(value) - minLog) / (maxLog - minLog);
}
